import { IAccount, IOwnershipFilter } from '../../../viewmodels/AppViewModels';
import { IFollowUpOptions } from '.';
import { ContactFilter } from './requests';

export type EmailAddress = {
	label?: string;
	value: string;
};

export enum HolidayId {
	Unknown = 'Unknown',
	NewYearsDay = 'NewYearsDay',
	Easter = 'Easter',
	MemorialDay = 'MemorialDay',
	IndependenceDay = 'IndependenceDay',
	LaborDay = 'LaborDay',
	Thanksgiving = 'Thanksgiving',
	Christmas = 'Christmas',
	IndependenceDayObserved = 'IndependenceDayObserved',
}

// Add holiday date lookup interface and function
export interface HolidayDate {
	id: HolidayId;
	name: string;
	date: Date;
}

export function getHolidayDates(startYear: number, endYear: number): HolidayDate[] {
	const holidays: HolidayDate[] = [];

	for (let year = startYear; year <= endYear; year++) {
		// New Year's Day - January 1st
		holidays.push({
			id: HolidayId.NewYearsDay,
			name: "New Year's Day",
			date: new Date(year, 0, 1), // January 1st
		});

		// Memorial Day - Last Monday in May
		const memorialDay = new Date(year, 4, 31); // May 31st
		memorialDay.setDate(31 - ((memorialDay.getDay() + 6) % 7)); // Find last Monday
		holidays.push({
			id: HolidayId.MemorialDay,
			name: 'Memorial Day',
			date: memorialDay,
		});

		// Independence Day - July 4th
		holidays.push({
			id: HolidayId.IndependenceDay,
			name: 'Independence Day',
			date: new Date(year, 6, 4), // July 4th
		});

		// Labor Day - First Monday in September
		const laborDay = new Date(year, 8, 1); // September 1st
		const dayOfWeek = laborDay.getDay(); // 0 = Sunday, 1 = Monday, etc.
		const daysUntilMonday = dayOfWeek === 0 ? 1 : dayOfWeek === 1 ? 0 : 8 - dayOfWeek;
		laborDay.setDate(1 + daysUntilMonday);

		holidays.push({
			id: HolidayId.LaborDay,
			name: 'Labor Day',
			date: laborDay,
		});

		// Thanksgiving - Fourth Thursday in November
		const thanksgiving = new Date(year, 10, 1); // November 1st
		const firstThursday = 1 + ((4 - thanksgiving.getDay() + 7) % 7);
		thanksgiving.setDate(firstThursday + 21); // Fourth Thursday
		holidays.push({
			id: HolidayId.Thanksgiving,
			name: 'Thanksgiving',
			date: thanksgiving,
		});

		// Christmas - December 25th
		holidays.push({
			id: HolidayId.Christmas,
			name: 'Christmas',
			date: new Date(year, 11, 25), // December 25th
		});

		// Easter - Complex calculation
		const easter = calculateEaster(year);
		holidays.push({
			id: HolidayId.Easter,
			name: 'Easter',
			date: easter,
		});
	}

	return holidays.sort((a, b) => a.date.getTime() - b.date.getTime());
}

// Holiday impact checking interface and function
export interface HolidayImpactResult {
	impacted: boolean;
	holidays: HolidayDate[];
}

export interface HolidayImpactOptions {
	observeHolidays?: boolean;
	observedHolidayIds?: HolidayId[];
	startDate: Date;
	endDate: Date;
	holidayDates: HolidayDate[];
	bufferDays?: number; // Number of days before/after to consider as impact
}

// Holiday account settings utility functions
export function doesAccountObserveHolidays(account?: IAccount): boolean {
	const sendEmailFeatures = account?.features?.sendEmail;

	// If observeHolidays is explicitly set, use that value
	if (sendEmailFeatures?.observeHolidays !== undefined) {
		return sendEmailFeatures.observeHolidays;
	}

	// Default to observeSendIntervals (business hours) if observeHolidays is not set
	return sendEmailFeatures?.observeSendIntervals || false;
}

export function getObservedHolidays(account?: IAccount): HolidayId[] {
	const sendEmailFeatures = account?.features?.sendEmail;

	// If observedHolidays is null or undefined, observe all holidays
	if (!sendEmailFeatures?.observedHolidays) {
		return Object.values(HolidayId).filter(id => id !== HolidayId.Unknown);
	}

	return sendEmailFeatures.observedHolidays;
}

export function checkHolidayImpact(options: HolidayImpactOptions): HolidayImpactResult {
	const {
		observeHolidays = false,
		observedHolidayIds = [],
		startDate,
		endDate,
		holidayDates,
		bufferDays = 2,
	} = options;

	if (!observeHolidays || !holidayDates || holidayDates.length === 0) {
		return { impacted: false, holidays: [] };
	}

	// Filter holidays to only those being observed
	const relevantHolidays = holidayDates.filter(holiday => observedHolidayIds.includes(holiday.id));

	if (relevantHolidays.length === 0) {
		return { impacted: false, holidays: [] };
	}

	// Check if any observed holidays fall within or close to the send period
	const impactedHolidays = relevantHolidays.filter(holiday => {
		const holidayTime = holiday.date.getTime();
		const startTime = startDate.getTime();
		const endTime = endDate.getTime();

		// Calculate buffer period in milliseconds
		const bufferMs = bufferDays * 24 * 60 * 60 * 1000;

		// Check if holiday is within the send period or within buffer days before/after
		return holidayTime >= startTime - bufferMs && holidayTime <= endTime + bufferMs;
	});
	if (impactedHolidays.length === 0) {
		return { impacted: false, holidays: [] };
	}

	return {
		impacted: true,
		holidays: impactedHolidays,
	};
}

// Easter calculation using the algorithm
function calculateEaster(year: number): Date {
	const a = year % 19;
	const b = Math.floor(year / 100);
	const c = year % 100;
	const d = Math.floor(b / 4);
	const e = b % 4;
	const f = Math.floor((b + 8) / 25);
	const g = Math.floor((b - f + 1) / 3);
	const h = (19 * a + b - d - g + 15) % 30;
	const i = Math.floor(c / 4);
	const k = c % 4;
	const l = (32 + 2 * e + 2 * i - h - k) % 7;
	const m = Math.floor((a + 11 * h + 22 * l) / 451);
	const month = Math.floor((h + l - 7 * m + 114) / 31);
	const day = ((h + l - 7 * m + 114) % 31) + 1;
	return new Date(year, month - 1, day);
}

export interface PreviewContactOwnersRequest {
	filter?: ContactFilter;
	contactIdsToInclude?: string[];
	contactIdsToOmit?: string[];
	ownershipFilter?: IOwnershipFilter;
	followUp?: IFollowUpOptions;
}
